import { parseUrlParams } from "../utils/url";
import { cmdKey } from "../utils/keyboard";
import BasicRecord from "../utils/BasicRecord";

declare type RouterContext = {
    url: URL;
    match: string[];
    query: BasicRecord<string[]>;
    reload?: boolean;
    ev?: MouseEvent;
};

type RouteHandler = (
    handler: RouterContext
) => void;
type HistoryListener = (listener : { action: string; url: string; state: unknown; }) => void;

interface Route {
    regex: RegExp;
    handler: RouteHandler;
}

interface RouterLinkElement extends HTMLLinkElement {
    routerLink?: RouterLink;
}

class RouterLink {
    #element: RouterLinkElement;
    #router: Router;
    #boundClickHandler: (ev: MouseEvent) => void;

    constructor(element: HTMLLinkElement, router: Router) {
        this.#element = element;
        this.#router = router;

        // these are added because of preact's component recycling
        if (this.#element.routerLink) {
            this.#element.removeEventListener("click", this.#element.routerLink.#boundClickHandler);
        }
        this.#boundClickHandler = this.#clickHandler.bind(this);
        this.#element.routerLink = this;

        this.#element.addEventListener("click", this.#boundClickHandler);
    }

    get element() { return this.#element; }

    get navigationOperation() { return this.#element.dataset["navOp"]; }

    #clickHandler(ev: MouseEvent) {
        if (this.#element.href !== "#" && (!this.#router.match(this.#element.href) || cmdKey(ev))) {
            return;
        }

        ev.preventDefault();

        if (this.#element.href === window.location.href) {
            return;
        }

        const shouldPreventNavigation = this.#router.shouldPreventNavigation(this.#element.href, ev);
        if (shouldPreventNavigation) {
            return;
        }

        const options: { flowNavigation?: boolean; } = {};
        if ("flowNav" in this.#element.dataset) {
            options.flowNavigation = true;
        }

        const currentURL = location.href;

        switch (this.navigationOperation) {
            case "replace":
                this.#router.history.replace(this.#element.href, options);
                break;

            case "pop":
                this.#router.history.pop();
                break;

            case "push":
            default:
                this.#router.history.push(this.#element.href, options);
        }

        if ("back" in this.#element.dataset) {
            if (this.#element.dataset["back"] && this.#router.onPushToBackStack) {
                this.#router.onPushToBackStack(this.#element.dataset["back"], currentURL);
            }
        }
    }
}

class HistoryManager {
    #history: History;
    #listeners: {
        [idx: string]: HistoryListener;
    };
    #listenerCount: number;
    #flowNavigationStack: { url: string; state: unknown; }[];
    #router: Router;

    constructor(routerInstance: Router) {
        this.#history = window.history;
        this.#listeners = {};
        this.#listenerCount = 0;
        this.#flowNavigationStack = [];
        this.#router = routerInstance;

        window.addEventListener("popstate", this.#handlePopState.bind(this));
    }

    #actions = {
        PUSH_STATE: "PUSH_STATE",
        POP_STATE: "POP_STATE",
        REPLACE_STATE: "REPLACE_STATE"
    };

    attach(listener: HistoryListener) {
        const idx = this.#listenerCount;

        this.#listeners[idx] = listener;
        this.#listenerCount++;

        return idx;
    }

    detach(idx: number) {
        delete this.#listeners[idx];
    }

    push(url: string, {
        state,
        flowNavigation,
        data
    } : {
        state?: unknown;
        flowNavigation?: boolean;
        data?: BasicRecord;
    } = {}) {
        const currentURL = location.href;
        if (flowNavigation) {
            this.#flowNavigationStack.push({
                url: currentURL,
                state: this.#history.state
            });
        } else {
            this.#flowNavigationStack.length = 0;
        }

        this.#history.pushState(state, "", url);

        if (data?.["back"] && this.#router.onPushToBackStack) {
            this.#router.onPushToBackStack(data["back"], data?.["backURL"] || currentURL);
        }

        this.#notifyListeners({
            action: this.#actions.PUSH_STATE,
            state,
            url
        });
    }

    replace(url: string, {
        state,
        flowNavigation,
        data
    } : {
        state?: unknown;
        flowNavigation?: boolean;
        data?: BasicRecord;
    } = {}) {
        const currentURL = location.href;

        if (flowNavigation) {
            this.#flowNavigationStack.push({
                url: location.href,
                state: this.#history.state
            });
        } else {
            this.#flowNavigationStack.length = 0;
        }

        this.#history.replaceState(state, "", url);

        if (data?.["back"] && this.#router.onPushToBackStack) {
            this.#router.onPushToBackStack(data["back"], data?.["backURL"] || currentURL);
        }

        this.#notifyListeners({
            action: this.#actions.REPLACE_STATE,
            state,
            url
        });
    }

    pop() {
        if (!this.#flowNavigationStack.length) {
            return;
        }

        const { state, url } = this.#flowNavigationStack.pop()!;

        this.#history.pushState(state, "", url);

        this.#notifyListeners({
            action: this.#actions.PUSH_STATE,
            state,
            url: window.location.toString()
        });
    }

    #notifyListeners(event: { action: string; url: string; state: unknown; }) {
        Object.keys(this.#listeners).forEach(key => {
            this.#listeners[key](event);
        }, this);
    }

    #handlePopState(ev: { state?: unknown; }) {
        if (this.#router.onPopBackState) {
            this.#router.onPopBackState();
        }

        this.#notifyListeners({
            action: this.#actions.POP_STATE,
            state: ev.state,
            url: window.location.toString()
        });
    }
}

class Router {
    #routes: Route[];
    #preventions: Route[];
    #history: HistoryManager;
    #onSetBackButton: ((label: string, url: string) => void) | null;
    #onResetBackButton: (() => void) | null;

    constructor() {
        this.#routes = [];
        this.#preventions = [];
        this.#history = new HistoryManager(this);
        this.#onSetBackButton = null;
        this.#onResetBackButton = null;

        this.#history.attach((event: { url: string; }) => {
            this.#handle(event.url);
        });
    }

    get history() {
        return this.#history;
    }

    match(url: string | URL) {
        return !!this.#findRoutes(Router.#getUrl(url)).length;
    }

    route(regex: RegExp, handler: RouteHandler, { unshift }: { unshift: boolean; } = { unshift: false }) {
        this.#register({ regex, handler }, { unshift });

        return this;
    }

    start(root: Element) {
        this.#hookAppLinks(root);

        this.#handle(window.location.href);

        return this;
    }

    reload() {
        return this.#handle(window.location.href, { reload: true });
    }

    addPrevention(regex: RegExp, handler: RouteHandler) {
        return this.#preventions.push({ regex, handler });
    }

    set onPushToBackStack(callback: ((label: string, url: string) => void) | null) {
        this.#onSetBackButton = callback;
    }

    get onPushToBackStack(): ((label: string, url: string) => void) | null {
        return this.#onSetBackButton;
    }

    set onPopBackState(callback: (() => void) | null) {
        this.#onResetBackButton = callback;
    }

    get onPopBackState(): (() => void) | null {
        return this.#onResetBackButton;
    }

    shouldPreventNavigation(url: string, ev: MouseEvent) {
        const computedUrl = Router.#getUrl(url);

        return this.#preventions.find(({ regex, handler }) => {
            return regex.test(computedUrl.pathname) && handler({
                url: computedUrl,
                match: computedUrl.pathname.match(regex)!.slice(1),
                query: parseUrlParams(computedUrl.search),
                ev
            });
        });
    }

    static #getUrl(url: string | URL) {
        if (typeof url === "string") {
            return new URL(url, window.location.origin);
        }

        return url;
    }

    #createLink(element: Element) {
        return new RouterLink(element as HTMLLinkElement, this);
    }

    #register(route: Route, { unshift }: { unshift: boolean; }) {
        if (unshift) {
            this.#routes.unshift(route);
        } else {
            this.#routes.push(route);
        }
    }

    #findRoutes(url: URL) {
        return this.#routes.filter(item => item.regex.test(url.pathname));
    }

    #handle(href: string, { reload = false } = {}) {
        // This replace operation added in order to get rid of trailing slashes. It's a temporary solution.
        // When https://zeplin.atlassian.net/browse/WA-4060 is resolved this replace shouldn't be needed.
        const trimmedHref = href.replace(/\/$/, "");
        const url = Router.#getUrl(trimmedHref);
        const routes = this.#findRoutes(url);

        if (!routes.length) {
            location.assign(url.toString());
            return;
        }

        return routes.reduce((prevPromise, route) =>
            prevPromise.then(() =>
                route.handler({
                    url,
                    match: url.pathname.match(route.regex)!.slice(1),
                    query: parseUrlParams(url.search),
                    reload
                })
            ), Promise.resolve()
        );
    }

    #searchAppLinks(node: HTMLElement): HTMLElement[] {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return [];
        }

        if (node.hasAttribute("data-link-type") && node.dataset["linkType"] === "inApp") {
            return [node];
        }

        return Array.from(node.childNodes).reduce((elements: HTMLElement[], childNode) => {
            return elements.concat(this.#searchAppLinks(childNode as HTMLElement));
        }, []);
    }

    #hookAppLinks(root: Element) {
        const appLinks = root.querySelectorAll('[data-link-type="inApp"]');

        Array.from(appLinks).forEach(elem => {
            this.#createLink(elem);
        });

        const appLinkObserverInit = {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ["data-link-type"]
        };

        const appLinkObserver = new MutationObserver((mutations: MutationRecord[]) => {
            mutations.forEach(mutation => {
                if (mutation.type === "attributes") {
                    const mutationTarget = mutation.target as HTMLElement;
                    if (mutationTarget.dataset["linkType"] === "inApp") {
                        this.#createLink(mutationTarget);
                    }
                } else if (mutation.type === "childList") {
                    Array.from(mutation.addedNodes).forEach(child => {
                        this.#searchAppLinks(child as HTMLElement).forEach(node => {
                            this.#createLink(node);
                        });
                    });
                }
            });
        });

        appLinkObserver.observe(root, appLinkObserverInit);
    }
}

const router = new Router();

export { RouterContext };
export default router;
